import { useState } from 'react'
import { Stack, Group, Button, TextInput } from '@mantine/core'
import {
  IconServer, IconDatabase, IconBell, IconCloud, IconCpu, IconClock,
  IconChartBar, IconActivity, IconRoute, IconCalendar, IconHistory,
} from '@tabler/icons-react'
import {
  PageTitle, SectionCard, SectionAction, PanelGrid, MetaList, HairlineDivider,
  CountTileGrid, CountTile, StatusBadge, SeverityBadge, HealthRow, StatTile,
  Breadcrumbs, TabStrip, FilterChip, DataTable, EmptyState, Skeleton, SkeletonText,
  DetailShell, ActionMenu, NavGroup, NavItem,
  useThemeColors, categoricalSlots, RELIEF_REQUIRED_FROM_SLOT,
} from '@lighthouse/design'
import type { DataTableColumn, StatusRole, SeverityLevel } from '@lighthouse/design'

/**
 * Living reference for the shared design system.
 *
 * Every primitive appears here with all of its states, including the unhappy
 * ones (empty, loading) that mockups never show and reskins usually rot. Not in
 * the nav — a developer surface, reachable at /design-system.
 */

const STATUSES: StatusRole[] = ['good', 'warning', 'serious', 'critical']
const SEVERITIES: SeverityLevel[] = ['critical', 'high', 'medium', 'low']

interface DemoRow {
  id: string
  report: string
  owner: string
  status: StatusRole
  runs: number
}

const DEMO_ROWS: DemoRow[] = [
  { id: '1', report: 'Harvest Operations Summary', owner: 'Operations', status: 'good', runs: 1284 },
  { id: '2', report: 'AP Aging Report', owner: 'Controller', status: 'critical', runs: 12 },
  { id: '3', report: 'Inventory Summary', owner: 'Warehouse', status: 'warning', runs: 431 },
]

const COLUMNS: DataTableColumn<DemoRow>[] = [
  { key: 'report', header: 'Report', render: (r) => r.report },
  { key: 'owner', header: 'Owner', render: (r) => r.owner },
  { key: 'status', header: 'Health', render: (r) => <StatusBadge status={r.status} /> },
  { key: 'runs', header: 'Runs', numeric: true, render: (r) => r.runs.toLocaleString(), width: 90 },
]

/**
 * Which slots are actually below 3:1, per mode, measured on the card surface.
 * The chart-level rule stays "relief from slot 4 onward" — slots are assigned in
 * order, so a chart that reaches slot 7 has already used 4, 5 and 6 — but the
 * reference should show where the real constraint lives rather than flagging
 * eight swatches identically.
 */
const SUB_3TO1 = new Map<number, string>([
  [4, 'light 2.30:1'],
  [5, 'light 2.85:1'],
  [6, 'dark 2.92:1'],
])

function Swatch({ color, label, note }: { color: string; label: string; note?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 92 }}>
      <div style={{ height: 40, borderRadius: 4, backgroundColor: color, border: '1px solid var(--color-border)' }} />
      <span style={{ fontSize: 11, color: 'var(--color-text-primary)', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--color-text-secondary)' }}>{color}</span>
      {note && <span style={{ fontSize: 10, color: 'var(--status-warning-fg)' }}>{note}</span>}
    </div>
  )
}

export function DesignSystemPage() {
  const [tab, setTab] = useState('overview')
  const [chip, setChip] = useState('all')
  const tokens = useThemeColors()
  const slots = categoricalSlots(tokens)

  return (
    <Stack gap="lg" pb="xl">
      <PageTitle subtitle="Living reference for @lighthouse/design. Toggle light/dark in the header to check both modes.">
        Design System
      </PageTitle>

      {/* ---- Typography ---- */}
      <SectionCard title="Typography">
        <Stack gap="xs">
          <span className="lh-display-title">Display — EB Garamond, 18px and up only</span>
          <span style={{ fontFamily: 'var(--font-ui)', fontSize: 14, color: 'var(--color-text-primary)' }}>
            UI / body — Inter. Everything dense stays here: nav, labels, tables, buttons.
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Mono — run ids, correlation ids: ETL-20260729-00421
          </span>
          <span style={{ fontFamily: 'var(--font-ui)', fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-secondary)' }}>
            Tabular figures for columns: 1,284,003 / 0,000,111
          </span>
        </Stack>
      </SectionCard>

      {/* ---- Status vs severity ---- */}
      <PanelGrid>
        <SectionCard title="Status — health" subtitle="Reserved scale. Always icon + label.">
          <Group gap="xs">{STATUSES.map((s) => <StatusBadge key={s} status={s} />)}</Group>
        </SectionCard>
        <SectionCard title="Severity — signals" subtitle="A different scale on purpose; they sit side by side.">
          <Group gap="xs">{SEVERITIES.map((s) => <SeverityBadge key={s} severity={s} />)}</Group>
        </SectionCard>
      </PanelGrid>

      {/* ---- Stat tiles + the delta polarity point ---- */}
      <SectionCard
        title="Stat tiles"
        subtitle="Delta colour follows INTENT, not the arrow. Both rows below rise; only one is good."
        action={<SectionAction>View all →</SectionAction>}
      >
        <PanelGrid>
          <StatTile label="Records Processed" value="18.7M" delta={12.5} deltaPolarity="higher-is-better" />
          <StatTile label="Failed Records" value="24,231" delta={2.1} deltaPolarity="lower-is-better" />
          <StatTile label="Success Rate" value="99.87%" delta={-0.03} deltaPolarity="higher-is-better" />
          <StatTile label="Avg Duration" value="00:04:32" delta={-14} deltaPolarity="lower-is-better" />
          <StatTile label="System Health" value="98%" status="good" size="lg" />
          <StatTile label="Unchanged" value="14" delta={0} deltaPolarity="higher-is-better" sublabel="no change" />
        </PanelGrid>
      </SectionCard>

      {/* ---- Chart slots ---- */}
      <SectionCard
        title="Categorical chart slots"
        subtitle={`Assign in order, never cycled. A chart reaching slot ${RELIEF_REQUIRED_FROM_SLOT} or beyond owes a relief channel — visible direct labels or a table view. Flagged swatches are the ones actually below 3:1.`}
      >
        <Group gap="sm" wrap="wrap">
          {slots.map((c, i) => (
            <Swatch key={i} color={c} label={`slot-${i + 1}`} note={SUB_3TO1.get(i + 1)} />
          ))}
        </Group>
        <HairlineDivider />
        <Group gap="sm" wrap="wrap" mt="sm">
          <Swatch color={tokens.chartSeries1} label="series-1" note="status: good" />
          <Swatch color={tokens.chartSeries2} label="series-2" note="status: warning" />
          <Swatch color={tokens.chartSeries3} label="series-3" note="status: bad" />
          <Swatch color={tokens.chartGrid} label="grid" />
          <Swatch color={tokens.chartAxis} label="axis" />
        </Group>
      </SectionCard>

      {/* ---- Health rows + count tiles ---- */}
      <PanelGrid>
        <SectionCard title="System Health" flush action={<SectionAction>Details →</SectionAction>}>
          <HealthRow name="Automation Workers" status="good" icon={<IconCpu size={15} />} />
          <HealthRow name="Scheduler Service" status="good" icon={<IconClock size={15} />} />
          <HealthRow name="Data Warehouse" status="warning" statusLabel="Degraded" icon={<IconDatabase size={15} />} />
          <HealthRow name="Notification Service" status="good" icon={<IconBell size={15} />} />
          <HealthRow name="External Connections" status="serious" statusLabel="Elevated" icon={<IconCloud size={15} />} />
          <HealthRow name="File Storage" status="critical" statusLabel="Unavailable" icon={<IconServer size={15} />} />
        </SectionCard>

        <SectionCard title="Governance Queue" flush>
          <CountTileGrid>
            <CountTile value={5} label="Inactive Maintainers" hint="No active maintainer assigned." action={<SectionAction>Review →</SectionAction>} />
            <CountTile value={3} label="Missing Owners" hint="No business owner." action={<SectionAction>Review →</SectionAction>} />
            <CountTile value={4} label="Certification Due" hint="Awaiting review." action={<SectionAction>Review →</SectionAction>} />
            <CountTile value={2} label="Delisted Reports" hint="Removed from the catalog." action={<SectionAction>Review →</SectionAction>} />
          </CountTileGrid>
        </SectionCard>
      </PanelGrid>

      {/* ---- Meta lists ---- */}
      <PanelGrid>
        <SectionCard title="Execution Summary">
          <MetaList items={[
            { label: 'Status', value: <StatusBadge status="good" label="Completed" /> },
            { label: 'Duration', value: '00:07:05' },
            { label: 'Triggered By', value: 'Schedule' },
            { label: 'Worker', value: 'Worker A' },
            { label: 'Run', value: 'ETL-20260729-00421', mono: true },
          ]} />
        </SectionCard>
        <SectionCard title="Record Summary">
          <MetaList items={[
            { label: 'Extracted', value: '482,310' },
            { label: 'Transformed', value: '481,992' },
            { label: 'Rejected', value: '318' },
            { label: 'Unresolved', value: '17' },
            { label: 'Inserted', value: '301,202' },
            { label: 'Updated', value: '180,773' },
          ]} />
        </SectionCard>
        <SectionCard title="Execution Health">
          <MetaList items={[
            { label: 'Success Rate', value: '99.87%' },
            { label: 'Data Quality', value: <StatusBadge status="warning" /> },
            { label: 'Validation', value: <StatusBadge status="good" label="Completed" /> },
            { label: 'Warnings', value: '3' },
            { label: 'Errors', value: '0' },
          ]} />
        </SectionCard>
      </PanelGrid>

      {/* ---- Table: loaded / loading / empty ---- */}
      <SectionCard
        title="DataTable"
        flush
        subtitle="Numeric columns right-align with tabular figures."
      >
        <DataTable
          columns={COLUMNS}
          rows={DEMO_ROWS}
          rowKey={(r) => r.id}
          onRowClick={() => { /* hover affordance demo */ }}
          toolbar={
            <>
              <TextInput placeholder="Search reports…" size="xs" style={{ minWidth: 200 }} />
              {['all', 'published', 'delisted'].map((c) => (
                <FilterChip key={c} active={chip === c} onClick={() => setChip(c)}>{c}</FilterChip>
              ))}
            </>
          }
        />
      </SectionCard>

      <PanelGrid>
        <SectionCard title="DataTable — loading" flush>
          <DataTable columns={COLUMNS} rows={[]} rowKey={(r) => r.id} loading skeletonRows={3} />
        </SectionCard>
        <SectionCard title="DataTable — empty" flush>
          <DataTable
            columns={COLUMNS}
            rows={[]}
            rowKey={(r) => r.id}
            empty={<EmptyState title="No reports yet" message="Reports appear here once someone publishes one from Cartographer." />}
          />
        </SectionCard>
      </PanelGrid>

      <PanelGrid>
        <SectionCard title="Skeletons">
          <SkeletonText lines={4} />
        </SectionCard>
        <SectionCard title="Empty state">
          <EmptyState
            title="No signals"
            message="Nothing needs attention right now."
            action={<Button size="compact-sm" variant="default" mt="xs">Refresh</Button>}
          />
        </SectionCard>
        <SectionCard title="Skeleton shapes">
          <Stack gap="xs">
            <Skeleton height={26} width="45%" />
            <Skeleton height={12} />
            <Skeleton height={12} width="80%" />
          </Stack>
        </SectionCard>
      </PanelGrid>

      {/* ---- Detail shell ---- */}
      <DetailShell
        breadcrumbs={<Breadcrumbs items={[
          { label: 'Operations', onClick: () => {} },
          { label: 'ETL Explorer', onClick: () => {} },
          { label: 'Inventory Transactions' },
        ]} />}
        title="Inventory Transactions"
        badges={<StatusBadge status="good" label="Completed" />}
        eyebrow="Run #ETL-20260729-00421"
        actions={
          <>
            <Button size="compact-sm" variant="default">Rerun</Button>
            <Button size="compact-sm" variant="default">View Logs</Button>
            <ActionMenu items={[
              { label: 'Open in Cartographer' },
              { label: 'Copy run id' },
              { label: 'Pause schedule', dividerBefore: true, disabled: true, disabledHint: 'Coming soon' },
            ]} />
          </>
        }
        tabs={
          <TabStrip
            value={tab}
            onChange={setTab}
            tabs={[
              { value: 'overview', label: 'Overview' },
              { value: 'details', label: 'Details' },
              { value: 'data', label: 'Data' },
              { value: 'logs', label: 'Logs' },
              { value: 'history', label: 'History' },
              { value: 'deps', label: 'Dependencies', disabled: true, disabledHint: 'Needs the dependency graph (W3)' },
            ]}
          />
        }
      >
        <span style={{ fontFamily: 'var(--font-ui)', fontSize: 12, color: 'var(--color-text-secondary)' }}>
          Active tab: <strong style={{ color: 'var(--color-text-primary)' }}>{tab}</strong>. This shell backs
          ETL Explorer, Pipeline View and Report Detail — built now so the design is proven once.
        </span>
      </DetailShell>

      {/* ---- Rail nav (on the navy rail, where it actually lives) ---- */}
      <SectionCard title="Rail navigation" subtitle="Shown on the rail background — it is constant navy in both modes.">
        <div style={{ backgroundColor: 'var(--rail-bg)', borderRadius: 6, padding: 12, maxWidth: 260 }}>
          <NavGroup label="Operations">
            <NavItem label="Overview" icon={<IconChartBar size={16} />} active />
            <NavItem label="Live Activity" icon={<IconActivity size={16} />} />
            <NavItem label="ETL Explorer" icon={<IconRoute size={16} />} state="coming-soon" comingSoonHint="Arrives with W2 run telemetry" />
            <NavItem label="Scheduler" icon={<IconCalendar size={16} />} state="coming-soon" />
            <NavItem label="Execution History" icon={<IconHistory size={16} />} />
          </NavGroup>
          <NavGroup label="Signals">
            <NavItem label="Signal Inbox" icon={<IconBell size={16} />} badge={3} />
            <NavItem label="Incidents" icon={<IconActivity size={16} />} badge={12} badgeTone="muted" />
          </NavGroup>
          <NavGroup label="System" defaultOpen={false}>
            <NavItem label="Hangfire" icon={<IconServer size={16} />} external />
          </NavGroup>
        </div>
      </SectionCard>
    </Stack>
  )
}
